import * as React from 'react';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import isItemSelected from './utils/isItemSelected';
import type CalendarSurfaceStore from './store/schema/CalendarSurfaceStore';
import type { SurfaceDecorator, SurfaceDecoratorProps } from './interfaces/SurfaceEventHandlers';
import { isItemMultiDay } from 'owa-calendar-events-visual-date-range';
import { observer } from 'mobx-react-lite';

import styles from './logic/SelectionStyles.scss';
import classnamesBind from 'classnames/bind';
let classNames = classnamesBind.bind(styles);

export function createItemsDecorator(
    surfaceStore: CalendarSurfaceStore,
    resizeOnHoverEnabled: (item: CalendarEvent) => boolean
): SurfaceDecorator<CalendarEvent> {
    return observer(({ item, ...divProps }: SurfaceDecoratorProps<CalendarEvent>) => {
        let isSelected = isItemSelected(surfaceStore, item);
        let isHovered =
            surfaceStore.hoveredItem &&
            item &&
            surfaceStore.hoveredItem.ItemId.Id == item.ItemId.Id;
        let isFocused =
            surfaceStore.focusedSurfaceElement &&
            (surfaceStore.focusedSurfaceElement as string).indexOf(item.ItemId.Id) >= 0;
        let expandable = resizeOnHoverEnabled?.(item);
        let itemClass = getDecoratorItemClass(
            item,
            isSelected,
            isHovered,
            isFocused,
            false /*isDragHelperItem*/,
            false /* isMonthItem */,
            expandable
        );

        return <div {...divProps} className={itemClass} data-calitemid={item.ItemId.Id} />;
    });
}

export function createMonthItemsDecorator(
    surfaceStore: CalendarSurfaceStore
): SurfaceDecorator<CalendarEvent> {
    return observer(({ item, ...divProps }: SurfaceDecoratorProps<CalendarEvent>) => {
        let isSelected = isItemSelected(surfaceStore, item);
        let isFocused =
            surfaceStore.focusedSurfaceElement &&
            (surfaceStore.focusedSurfaceElement as string).indexOf(item.ItemId.Id) >= 0;
        let isHovered =
            surfaceStore.hoveredItem &&
            item &&
            surfaceStore.hoveredItem.ItemId.Id == item.ItemId.Id;
        let itemClass = getDecoratorItemClass(
            item,
            isSelected,
            isHovered,
            isFocused,
            false /*isDragHelperItem*/,
            true /* isMonthItem */
        );

        return <div {...divProps} className={itemClass} data-calitemid={item.ItemId.Id} />;
    });
}

/**
 * Creates a decorator for a drag helper item
 */
export function createDragHelperDecorator(
    surfaceStore: CalendarSurfaceStore
): SurfaceDecorator<CalendarEvent> {
    return ({ item, ...divProps }: SurfaceDecoratorProps<CalendarEvent>) => {
        let itemClass = getDecoratorItemClass(
            item,
            true /* isSelected */,
            true /* isHovered */,
            false /* isFocused */,
            true /* isDragHelperItem */
        );
        return <div {...divProps} className={itemClass} />;
    };
}

function getDecoratorItemClass(
    item: CalendarEvent,
    isSelected: boolean,
    isHovered: boolean,
    isFocused: boolean,
    isDragHelperItem: boolean = false,
    isMonthItem: boolean = false,
    isExpandable: boolean = false
) {
    return classNames(
        styles.item,
        'calendar-SelectionStyles-resizeBoxParent',
        { monthItem: isMonthItem },
        { itemHovered: isHovered },
        { itemSelected: isSelected },
        { itemFocused: isFocused },
        { isAllDayItem: item.IsAllDayEvent },
        { isMultiDayItem: isItemMultiDay(item) },
        { itemDragHelper: isDragHelperItem },
        { expandable: isExpandable }
    );
}
